/**
 * Assemble a desktop video-generation project from conversation files,
 * Work artifacts, and shot-script text. Pure helpers — no React, no I/O.
 *
 * The viewer treats a generated film like a deck or document: one artifact
 * with a timeline of clips, keyframe stills, character refs, and the
 * approved shot list.
 */

import { artifactBaseName, isScratchVideoClipName, isVideoArtifact } from '@/components/Coworker/coworkerArtifactTypes';

const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|mkv|avi)$/i;
const IMAGE_EXT_RE = /\.(png|jpe?g|webp|gif)$/i;
const SEGMENT_RE = /(?:^|\/)segment[_-]?(\d+)\.(mp4|mov|m4v|webm|mkv)$/i;
const SHOT_CLIP_RE = /(?:^|\/)(?:tmp\/|scratch\/)?(?:shot|clip|scene)[_-]?(\d+)/i;
const NORMALIZED_RE = /(?:^|\/)(?:norm(?:alized)?_)(?:clip)?(\d+)/i;
// Underscores are word characters, so \bfilm\b misses work_film_v2.mp4.
// A named deliverable can live in tmp/ just like its source shots.
const FILM_NAME_RE = /(?:^|[\s_.-])(?:film|episode|trailer|sizzle|final|export)(?=[\s_.-]|$)/i;
const INTERMEDIATE_NAME_RE = /(?:^|[\s_.-])(?:raw|intermediate|concat|normalized)(?=[\s_.-]|$)/i;
const KEYFRAME_RE = /(?:shot|clip|scene)[_-]?0*(\d+)[_-]?(?:keyframe|grab|still)/i;
const MASTER_RE = /(?:^|\/|_)([a-z0-9]+)[_-]master(?:_v\d+)?(?:_c\d+)?(?:_drifted)?\.(png|jpe?g|webp|gif)$/i;
const SHEET_RE = /(?:^|\/|_)([a-z0-9]+)[_-]sheet(?:_v\d+)?\.(png|jpe?g|webp|gif)$/i;
const REFS_PATH_RE = /(?:^|\/)refs\//i;
const SHOT_HEADING_RE = /(?:^|\n)\s*(?:#{1,6}\s+)?(?:\*{1,3}|_{1,3})?\s*(?:shot|clip)\s+(\d+)\b([^\n]*)/gi;
const SHOT_ROW_RE = /^\s*(?:shot|clip)\s+(\d+)\s*\|(.+)$/gim;
const SHOT_FIELD_RE = /^(camera|action|line|dialogue|prompt|duration|time|wardrobe|location)\s*:\s*(.+)$/i;
const GENERATED_SOURCES = new Set(['inference', 'ai_generated', 'ai-generated']);

export function mediaBaseName(item) {
  return artifactBaseName(item)
    || String(item?.id || '').split(/[/\\]/).pop().split('?')[0]
    || '';
}

function mediaPath(item) {
  return String(item?.path || item?.originalName || item?.name || item?.cdnPath || item?.url || item?.id || '');
}

export function isVideoMedia(item) {
  if (!item) return false;
  if (isVideoArtifact(item)) return true;
  const mime = String(item.mimeType || item.type || '').toLowerCase();
  if (mime.startsWith('video/')) return true;
  if (item.fileType === 'video' || item.type === 'video') return true;
  return VIDEO_EXT_RE.test(mediaBaseName(item) || mediaPath(item));
}

export function isImageMedia(item) {
  if (!item) return false;
  if (isVideoMedia(item)) return false;
  const mime = String(item.mimeType || item.type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  if (item.fileType === 'image' || item.type === 'image') return true;
  return IMAGE_EXT_RE.test(mediaBaseName(item) || mediaPath(item));
}

function firstMatch(values, regex) {
  for (const value of values) {
    const match = String(value || '').match(regex);
    if (match) return Number(match[1]);
  }
  return null;
}

export function extractShotIndex(item) {
  const values = [mediaBaseName(item), mediaPath(item)];
  return firstMatch(values, KEYFRAME_RE)
    ?? firstMatch(values, SHOT_CLIP_RE)
    ?? firstMatch(values, SEGMENT_RE)
    ?? firstMatch(values, NORMALIZED_RE);
}

export function classifyVideoRole(item) {
  if (!isVideoMedia(item)) return null;
  const path = mediaPath(item);
  const name = mediaBaseName(item);
  const pathName = path.split(/[/\\]/).pop().split('?')[0];
  const names = [name, pathName];
  if (names.some((value) => SEGMENT_RE.test(value))) return 'segment';
  // Shot identity wins over suffixes such as shot01_final.mp4.
  if (names.some((value) => SHOT_CLIP_RE.test(value) || NORMALIZED_RE.test(value))) return 'clip';
  if (names.some((value) => FILM_NAME_RE.test(value))) return 'film';
  // export_artifact preserves the workspace folder. A named movie exported
  // from tmp/ is still the finished movie, not an extra shot in the timeline.
  if (names.some((value) => isScratchVideoClipName(value) || INTERMEDIATE_NAME_RE.test(value))) {
    return 'clip';
  }
  return 'film';
}

export function classifyStillKind(item) {
  if (!isImageMedia(item)) return null;
  const path = mediaPath(item);
  const name = mediaBaseName(item);
  const haystack = `${path} ${name}`;
  if (KEYFRAME_RE.test(haystack)) return 'keyframe';
  if (MASTER_RE.test(name) || MASTER_RE.test(path)) return 'master';
  if (SHEET_RE.test(name) || SHEET_RE.test(path)) return 'sheet';
  if (REFS_PATH_RE.test(path)) return 'ref';
  return 'still';
}

export function stillCharacterName(item) {
  const name = mediaBaseName(item);
  const path = mediaPath(item);
  const master = name.match(MASTER_RE) || path.match(MASTER_RE);
  if (master) return master[1];
  const sheet = name.match(SHEET_RE) || path.match(SHEET_RE);
  if (sheet) return sheet[1];
  return '';
}

export function isVideoProjectStill(item) {
  const kind = classifyStillKind(item);
  return kind === 'keyframe' || kind === 'master' || kind === 'sheet' || kind === 'ref';
}

export function looksLikeVideoProjectName(item) {
  const path = mediaPath(item);
  const name = mediaBaseName(item);
  if (!name && !path) return false;
  if (SEGMENT_RE.test(name) || SEGMENT_RE.test(path)) return true;
  if (SHOT_CLIP_RE.test(name) || SHOT_CLIP_RE.test(path)) return true;
  if (isScratchVideoClipName(name) || isScratchVideoClipName(path)) return true;
  if (KEYFRAME_RE.test(`${path} ${name}`)) return true;
  if (MASTER_RE.test(name) || SHEET_RE.test(name) || REFS_PATH_RE.test(path)) return true;
  return FILM_NAME_RE.test(name);
}

export const videoMediaOrigin = file => String(file?.origin || file?.parameters?.origin || '').toLowerCase();
export const isStudioVideo = file => isVideoMedia(file) && (file?.studio === true || file?.parameters?.studio === true);

export function isGeneratedVideoFile(file) {
  if (!isVideoMedia(file)) return false;
  // Provenance from the orchestrator: a file the sandbox fetched from the web
  // (download_video / fetch_url) is downloaded, not generated — it never opens
  // the video studio on its own. The agent can still force it in with an
  // explicit studio flag on export (a downloaded clip being edited into a
  // project IS studio work).
  if (isStudioVideo(file)) return true;
  if (videoMediaOrigin(file) === 'downloaded' || file?.studio === false || file?.parameters?.studio === false) return false;
  const source = String(file?.source || '').toLowerCase();
  if (source === 'upload') return false;
  if (GENERATED_SOURCES.has(source)) return true;
  if (file?.path && !file?.cdnPath) return true;
  if (looksLikeVideoProjectName(file)) return true;
  return file?.fileType === 'video' || file?.type === 'video';
}

export function firstVideoProjectMedia({ artifacts = [], files = [] } = {}) {
  const pool = [
    ...(Array.isArray(artifacts) ? artifacts : []),
    ...(Array.isArray(files) ? files : []),
  ];
  return pool.find((item) => isVideoProjectStill(item) || isGeneratedVideoFile(item)) || null;
}

/** True when the assembled studio has something the sidebar can open. */
export function videoProjectIsOpenable(project) {
  if (!project) return false;
  if (firstVideoProjectMedia(project)) return true;
  return !!(
    project.film
    || (Array.isArray(project.finals) && project.finals.length)
    || (Array.isArray(project.stills) && project.stills.length)
    || (Array.isArray(project.clips) && project.clips.length)
    || (Array.isArray(project.segments) && project.segments.length)
    || (Array.isArray(project.characters) && project.characters.length)
    || (Array.isArray(project.keyframes) && project.keyframes.length)
  );
}

/** Explicitly standalone or downloaded media outside a production project. */
function isStandaloneVideo(item) {
  return isVideoMedia(item)
    && (videoMediaOrigin(item) === 'downloaded' || item?.studio === false || item?.parameters?.studio === false)
    && !isStudioVideo(item);
}

/**
 * Should this conversation show the media studio sidebar, and which kind?
 * Layered, most-explicit first:
 *   1. Agent-declared video project (video plan film/clips/segments, or an
 *      export_artifact studio flag) → 'video'.
 *   2. A shot script with media → 'video' (legacy production projects).
 *   3. Images + a user request for a video → 'video' (stills project).
 *   4. Images otherwise → 'image'. Nothing → null.
 * A plain downloaded video is none of these — it renders as an artifact.
 */
export function conversationMediaStudio({ project, files = [], messages = [] } = {}) {
  const media = [...files, ...(project?.stills || [])];
  const projectMedia = [project?.film, ...(project?.clips || []), ...(project?.segments || [])].filter(Boolean);
  if (files.some(isStudioVideo) || projectMedia.some(item => isStudioVideo(item.source || item))) return 'video';
  // A finished file is not a production plan. Legacy projects can still be
  // recognized by their shot script; a lone downloaded/edited MP4 cannot.
  if (project?.shots?.length && (projectMedia.length || media.some(isImageMedia))) return 'video';
  if (!media.some(isImageMedia)) return null;
  const videoRequest = messages.some((message) => {
    if (message?.role !== 'user') return false;
    const text = messagePlainText(message);
    // A video game, thumbnail, or written video script is not a rendered video.
    if (/\b(?:video game|thumbnail|script only|only (?:a |the )?script)\b/i.test(text)) return false;
    return /\b(?:create|make|generate|produce|render|animate)\b[^.!?\n]{0,100}\b(?:video|film|movie|animation)\b/i.test(text)
      || /\b(?:stills?|keyframes?|storyboard)\b[^.!?\n]{0,60}\b(?:for (?:a |the |my )?(?:video|film|movie)|video generation)\b/i.test(text);
  });
  return videoRequest || project?.shots?.length ? 'video' : 'image';
}

export function isGeneratedVideoProjectFile(file, opts = {}) {
  if (opts.videoProject === false) return false;
  if (opts.videoProject === true) return isVideoMedia(file) || isImageMedia(file);
  if (isStandaloneVideo(file)) return false;
  if (opts.artifacts || opts.files || opts.scriptText || opts.taskId) {
    return isVideoMedia(file) || isVideoProjectStill(file) || hasProjectMedia(opts);
  }
  if (isVideoProjectStill(file)) return true;
  return isGeneratedVideoFile(file);
}

function hasProjectMedia(opts = {}) {
  const artifacts = Array.isArray(opts.artifacts) ? opts.artifacts : [];
  const files = Array.isArray(opts.files) ? opts.files : [];
  return artifacts.some((item) => isVideoMedia(item) || isVideoProjectStill(item))
    || files.some((item) => isVideoMedia(item) || isVideoProjectStill(item));
}

function messagePlainText(message) {
  const raw = message?.content;
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    return raw
      .filter((part) => part && (part.type === 'text' || typeof part.text === 'string'))
      .map((part) => part.text || '')
      .join('\n');
  }
  return '';
}

const STATUS_LOG_RE = /polling for|is generating|take \d+ landed|http \d{3}|failed to build|has audio\s*\(|extracting voice|gateway error|transient|job id|mid-word|re-extract|\(QA retake\)|spend (?:was |is )?declined|no credits (?:were )?spent|complete and held|not yet started|gate-passed|(?:clips?|takes?|redos?) (?:are |is )?(?:complete|exported|on disk)|(?:original|earlier|previous) takes?[^\n]{0,40}untouched/i;

function looksLikeStatusLog(text) {
  return STATUS_LOG_RE.test(String(text || ''));
}

function headingLooksLikeShot(text) {
  const value = String(text || '').replace(/^\s*(?:>\s*)+/gm, '');
  const hasShotList = /^\s*(?:#{1,6}\s+|\*{1,3})?(?:shot list|shot breakdown|storyboard)\b/im.test(value);
  const rows = [...value.matchAll(new RegExp(SHOT_ROW_RE.source, 'gim'))];
  if (rows.length >= 2) return true;
  const headings = [...value.matchAll(new RegExp(SHOT_HEADING_RE.source, 'gi'))];
  let scripted = 0;
  for (const match of headings) {
    const extra = match[2] || '';
    const after = value.slice(match.index + match[0].length, match.index + match[0].length + 160);
    const blob = `${extra}\n${after}`;
    if (looksLikeStatusLog(blob)) continue;
    if (
      /^(camera|action|line|dialogue|prompt|duration|time|wardrobe|location)\s*:/im.test(blob)
      || /\s*\|/.test(extra)
      // A dash after "Shot 1" also occurs in delivery/status summaries.
      // Unlabelled headings need a real timing range to qualify as a script.
      || /\b\d+(?:\.\d+)?\s*[—–-]\s*\d+(?:\.\d+)?\s*s\b/i.test(extra)
      // Production briefs also give per-clip lengths: "Clip 1 — 12s".
      // Require a shot-list section so delivery summaries stay out.
      || (hasShotList && /\b\d+(?:\.\d+)?\s*s\b/i.test(extra))
    ) scripted += 1;
  }
  return scripted >= 2;
}

function shotScriptScore(shot) {
  if (!shot) return 0;
  let score = 0;
  if (shot.camera) score += 3;
  if (shot.line) score += 3;
  if (shot.prompt) score += 2;
  if (shot.duration) score += 1;
  if (shot.action && !looksLikeStatusLog(shot.action) && shot.action.length < 320) score += 2;
  if (looksLikeStatusLog(shot.action) || looksLikeStatusLog(shot.body)) score -= 6;
  return score;
}

function scriptQuality(text) {
  const shots = parseShotScript(text);
  if (shots.length < 2) return 0;
  return shots.reduce((sum, shot) => sum + Math.max(0, shotScriptScore(shot)), 0);
}

function parseShotFields(body) {
  const fields = {
    camera: '',
    action: '',
    line: '',
    wardrobe: '',
    location: '',
    duration: '',
    prompt: '',
  };
  const leftover = [];
  for (const rawLine of String(body || '').split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    const match = line.match(SHOT_FIELD_RE);
    if (!match) {
      leftover.push(line);
      continue;
    }
    const key = match[1].toLowerCase();
    const value = match[2].trim();
    if (key === 'dialogue') fields.line = value;
    else if (key === 'time') fields.duration = value;
    else if (key === 'prompt') fields.prompt = value;
    else if (Object.prototype.hasOwnProperty.call(fields, key)) fields[key] = value;
  }
  return { ...fields, body: leftover.join('\n') };
}

function parseTimeRange(headingExtra, body) {
  const fromHeading = String(headingExtra || '').match(/\(?(\d+\s*[–—-]\s*\d+\s*s)\)?/i);
  if (fromHeading) return fromHeading[1].replace(/\s+/g, '');
  const fromBody = String(body || '').match(/\b(\d+\s*[–—-]\s*\d+\s*s)\b/i);
  if (fromBody) return fromBody[1].replace(/\s+/g, '');
  const duration = String(headingExtra || '').match(/\b(\d+(?:\.\d+)?\s*s)\b/i);
  return duration ? duration[1].replace(/\s+/g, '') : '';
}

export function parseShotScript(text) {
  const value = String(text || '').replace(/^\s*(?:>\s*)+/gm, '').trim();
  if (!value) return [];
  const byIndex = new Map();

  const remember = (shot) => {
    if (!Number.isFinite(shot.index) || shot.index < 0) return;
    const prev = byIndex.get(shot.index);
    if (!prev) {
      byIndex.set(shot.index, shot);
      return;
    }
    const nextScore = shotScriptScore(shot);
    const prevScore = shotScriptScore(prev);
    if (nextScore !== prevScore) {
      byIndex.set(shot.index, nextScore > prevScore ? { ...prev, ...shot } : { ...shot, ...prev });
      return;
    }
    const richer = [shot.camera, shot.action, shot.line, shot.body].join('').length
      >= [prev.camera, prev.action, prev.line, prev.body].join('').length;
    byIndex.set(shot.index, richer ? { ...prev, ...shot } : { ...shot, ...prev });
  };

  for (const match of value.matchAll(SHOT_ROW_RE)) {
    const index = Number(match[1]);
    const cells = String(match[2] || '').split('|').map((cell) => cell.trim()).filter(Boolean);
    remember({
      index,
      heading: `Shot ${index}`,
      duration: cells[0] || '',
      camera: cells[1] || '',
      action: cells[2] || '',
      wardrobe: (cells.find((cell) => /^wardrobe:/i.test(cell)) || '').replace(/^wardrobe:\s*/i, ''),
      location: cells[3] && !/^(wardrobe|refs|keep|line):/i.test(cells[3]) ? cells[3] : '',
      line: '',
      prompt: '',
      body: cells.slice(3).join(' · '),
    });
  }

  const headingMatches = [...value.matchAll(SHOT_HEADING_RE)];
  for (let i = 0; i < headingMatches.length; i += 1) {
    const match = headingMatches[i];
    if (value[match.index] === '|' || /^\s*(?:shot|clip)\s+\d+\s*\|/i.test(match[0])) {
      // Planner rows are already handled above.
      continue;
    }
    const index = Number(match[1]);
    const extra = match[2] || '';
    const extraClean = String(extra)
      .replace(/^\s*\(?\d+\s*[–—-]\s*\d+\s*s\)?\s*[—–-]*\s*/i, '')
      .replace(/^\*+\s*/g, '')
      .replace(/^[—–-]+\s*/, '')
      .replace(/\*+\s*$/, '')
      .trim();
    const start = match.index + match[0].length;
    const end = i + 1 < headingMatches.length ? headingMatches[i + 1].index : value.length;
    const chunk = value.slice(start, end);
    const fields = parseShotFields(chunk);
    const action = fields.action
      || (!looksLikeStatusLog(extraClean) ? extraClean : '');
    const body = (!looksLikeStatusLog(fields.body) ? fields.body : '')
      || (!looksLikeStatusLog(extraClean) ? extraClean : '');
    remember({
      index,
      heading: `Shot ${index}`,
      duration: fields.duration || parseTimeRange(extra, chunk),
      camera: fields.camera,
      action,
      line: fields.line,
      wardrobe: fields.wardrobe,
      location: fields.location,
      prompt: fields.prompt,
      body,
    });
  }

  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}

function mediaKey(item) {
  return mediaAliasKeys(item)[0] || '';
}

function mediaAliasKeys(item) {
  const keys = [];
  // Workspace and CDN paths are case-sensitive. A differently cased export
  // may be a distinct cut (for example, the later version with audio).
  const path = String(item?.path || '');
  if (path) keys.push(`path:${path}`);
  const name = mediaBaseName(item);
  if (name) keys.push(`name:${name}`);
  const url = String(item?.cdnPath || item?.url || item?.src || item?.uri || '')
    .split('?')[0];
  if (url) keys.push(`url:${url}`);
  const id = item?.id || item?._id;
  if (id && !String(id).includes('/')) keys.push(`id:${id}`);
  return keys;
}

function preferRawItem(current, next) {
  if (!current) return next;
  const score = (item) => (
    (item?.path ? 2 : 0)
    + (mediaUrl(item) ? 1 : 0)
    + ((Number(item?.sizeInBytes || item?.size) || 0) > 0 ? 1 : 0)
  );
  const preferred = score(next) > score(current) ? next : current;
  const other = preferred === next ? current : next;
  // Workspace records carry the edit/stitch path, while the matching chat
  // File carries the playable CDN URL. Keep both when deduplicating them.
  return {
    ...preferred,
    ...(preferred.path || other.path ? { path: preferred.path || other.path } : {}),
    ...(!mediaUrl(preferred) && mediaUrl(other) ? { url: mediaUrl(other) } : {}),
  };
}

function mediaUrl(item) {
  return item?.cdnPath || item?.url || item?.src || item?.uri || '';
}

function toMediaItem(item, role, extras = {}) {
  const name = mediaBaseName(item) || 'file';
  const path = item?.path || (typeof item?.id === 'string' && item.id.includes('/') ? item.id : '');
  return {
    id: mediaKey(item) || `media:${role}:${name}`,
    name,
    path,
    url: mediaUrl(item),
    mimeType: item?.mimeType || item?.type || '',
    fileType: item?.fileType || role,
    size: Number(item?.sizeInBytes || item?.size) || 0,
    origin: item?.origin || null,
    studio: item?.studio === true,
    role,
    index: role === 'film' ? null : extras.index ?? extractShotIndex(item),
    kind: extras.kind || null,
    character: extras.character || '',
    source: item,
  };
}

// clip09.mp4 → 0; clip09_redo / clip09-1 / clip6_v3 / clip6_will_scream → >0
export function clipRevisionScore(name) {
  const base = String(name || '').split(/[/\\]/).pop() || '';
  const tagged = base.match(/^(?:shot|clip|scene)[_-]?0*(\d+)([^.]*)\./i);
  if (tagged) {
    // A still's role is not a revision. Otherwise both `_keyframe` and
    // `_keyframe_v2` score as an arbitrary suffix, so file size and lexical
    // order can make the original look like the latest take.
    const extra = (tagged[2] || '').replace(/^[_-](?:keyframe|still|grab)(?=[_-]|$)/i, '');
    if (!extra) return 0;
    const ver = extra.match(/^[_-](?:v|take[_-]?)?(\d+)$/i);
    if (ver) return 10 + Number(ver[1]);
    if (/^[_-](redo|revised|retry|new|fixed)/i.test(extra)) return 20;
    return 15;
  }
  const versioned = base.match(/[_-]v(\d+)\.[^.]+$/i);
  if (versioned) return 10 + Number(versioned[1]);
  const numbered = base.match(/-(\d{1,2})\.[^.]+$/);
  return numbered ? 10 + Number(numbered[1]) : 0;
}

function preferItem(current, next) {
  if (!current) return next;
  const currentRev = clipRevisionScore(current.name || current.path);
  const nextRev = clipRevisionScore(next.name || next.path);
  if (nextRev !== currentRev) return nextRev > currentRev ? next : current;
  const currentUrl = current.url ? 1 : 0;
  const nextUrl = next.url ? 1 : 0;
  if (nextUrl !== currentUrl) return nextUrl > currentUrl ? next : current;
  if ((next.size || 0) !== (current.size || 0)) return (next.size || 0) > (current.size || 0) ? next : current;
  return current;
}

function sortTakes(list) {
  return [...(list || [])].sort((a, b) => {
    const scoreDelta = clipRevisionScore(a.name || a.path) - clipRevisionScore(b.name || b.path);
    if (scoreDelta) return scoreDelta;
    return (a.name || '').localeCompare(b.name || '', undefined, { numeric: true });
  });
}

function finalTimestamp(item) {
  const source = item?.source || {};
  const raw = source.updatedAt || source.createdAt || source.timestamp;
  if (!raw) return 0;
  const numeric = typeof raw === 'number' ? raw : Date.parse(raw);
  return Number.isFinite(numeric) ? numeric : 0;
}

function sortFinals(films) {
  const hasCompleteDates = films.every((film) => finalTimestamp(film) > 0);
  return films.map((film, order) => ({ film, order }))
    .sort((a, b) => {
      // Reliable dates tell us which export arrived last. Otherwise prefer a
      // version suffix, then delivery order; file size says nothing about age.
      const aTime = finalTimestamp(a.film);
      const bTime = finalTimestamp(b.film);
      if (hasCompleteDates && aTime !== bTime) return bTime - aTime;
      const revision = clipRevisionScore(b.film.name) - clipRevisionScore(a.film.name);
      return revision || b.order - a.order;
    })
    .map(({ film }) => ({ ...film, status: 'ready', still: null, shot: null, label: 'Full film' }));
}

function pushTake(map, key, item) {
  const list = map.get(key) || [];
  const existing = list.findIndex((entry) => entry.id === item.id);
  if (existing >= 0) list[existing] = preferItem(list[existing], item);
  else list.push(item);
  map.set(key, list);
}

export function decorateTakes(takes, preferred = null) {
  const ordered = sortTakes(takes);
  const selected = preferred || ordered.reduce((best, item) => preferItem(best, item), null);
  return {
    takes: ordered.map((item, index) => ({
      ...item,
      takeIndex: index,
      takeLabel: ordered.length > 1 ? `Take ${index + 1}` : 'Take 1',
    })),
    selectedTakeId: selected?.id || null,
  };
}

export function applyVideoProjectTake(item, takeId) {
  if (!item?.takes?.length) return item;
  const take = item.takes.find((entry) => entry.id === takeId);
  if (!take) return item;
  return {
    ...item,
    name: take.name || item.name,
    path: take.path || item.path,
    url: take.url || item.url,
    mimeType: take.mimeType || item.mimeType,
    size: take.size || item.size,
    source: take.source || item.source,
    mediaId: take.id,
    selectedTakeId: take.id,
  };
}

// Previewing a take does not change the export. Use an explicit choice when
// present, otherwise the latest take, exactly as the stitch direction does.
export function videoProjectFinalTake(item, lockedTakeId) {
  const takes = item?.takes || [];
  return (lockedTakeId && takes.find((take) => take.id === lockedTakeId)) || takes[takes.length - 1] || null;
}

export function titleCaseWords(value) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}

export function displayStillLabel(item) {
  if (!item) return '';
  if (item.kind === 'master' || item.kind === 'sheet') {
    const who = titleCaseWords(item.character || 'Cast');
    return `${who} ${item.kind}`;
  }
  if (Number.isFinite(item.index)) return `Shot ${item.index} still`;
  return item.name || 'Still';
}

export function extractCharacterIdentity(scriptText, character) {
  const text = String(scriptText || '');
  const who = titleCaseWords(character);
  if (!text || !who) return '';
  const escaped = who.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const heading = text.match(new RegExp(
    `(?:identity(?:\\s+block)?|cast)\\s*[:\\-]?\\s*([^\\n]*${escaped}[^\\n]*)`,
    'i',
  ));
  if (!heading) return '';
  const after = text.slice(heading.index + heading[0].length);
  const body = after.match(/^\n([\s\S]{0,400}?)(?=\n\s*(?:#{1,6}\s+|identity|voice bible|shot|clip|style bible)\b|$)/i);
  const detail = [heading[1], body?.[1]].map((part) => String(part || '').trim()).filter(Boolean).join('\n');
  return [`${who} master reference still. Keep identity consistent.`, detail].filter(Boolean).join('\n');
}

export function stillGroupId(still) {
  if (!still) return '';
  if (still.kind === 'keyframe' && Number.isFinite(still.index)) return `still:keyframe:${still.index}`;
  if (still.kind === 'master' || still.kind === 'sheet') {
    return `still:${still.kind}:${String(still.character || still.name || '').toLowerCase()}`;
  }
  return still.id || '';
}

export function seedVideoRedoPrompt(item) {
  if (!item) return '';
  const shot = item.shot;
  const scene = shot
    ? [
      shot.prompt,
      shot.action && !shot.prompt ? shot.action : '',
      shot.body && !shot.prompt && !shot.action ? shot.body : '',
      shot.camera,
      shot.line,
      [shot.wardrobe, shot.location].filter(Boolean).join(' · '),
    ].filter(Boolean).join('\n')
    : '';
  if (scene) return scene;
  if (item.prompt) return item.prompt;
  if (item.kind === 'master' || item.kind === 'sheet') {
    const who = item.character || 'character';
    return `${who} ${item.kind} reference still. Keep identity consistent.`;
  }
  if (Number.isFinite(item.index)) return `Shot ${item.index} keyframe. Match the approved character identity.`;
  return item.name || '';
}

const SPEND_QUESTION_KINDS = new Set(['credits', 'cost', 'memory', 'plan']);

function optionLabel(option) {
  if (option == null) return '';
  if (typeof option === 'string' || typeof option === 'number') return String(option);
  return String(option.label || option.id || '');
}

/**
 * Map a studio Approve / Redo line onto an open ask_user card.
 * Matching option labels become optionId answers. Custom redo text answers
 * choice/script cards. Spend / credits gates stay untouched so a redo cannot
 * approve a 720-credit motion spend.
 */
export function studioDirectionQuestionAnswer(question, text) {
  if (!question || question.orphaned) return null;
  const trimmed = String(text || '').trim();
  if (!trimmed) return null;
  const kind = String(question.kind || 'choice');
  const options = Array.isArray(question.options) ? question.options : [];
  const lower = trimmed.toLowerCase();
  const firstLine = lower.split(/\r?\n/, 1)[0].trim();
  const match = options.find((option) => {
    const label = optionLabel(option).toLowerCase();
    // Studio directions can mention approval while explicitly withholding it.
    // A money/plan gate needs the requested answer itself, not an incidental
    // word such as "approve" inside a take-selection or redo instruction.
    return label && (label === lower || label === firstLine);
  });
  if (match) {
    const optionId = typeof match === 'object' ? (match.id || match.label) : match;
    return { id: question.id || '', answer: trimmed, optionId };
  }
  if (SPEND_QUESTION_KINDS.has(kind)) return null;
  return { id: question.id || '', answer: trimmed };
}

export function describeVideoTakeChoice(item) {
  if (!item) return '';
  const take = item.takes?.find((entry) => entry.id === item.selectedTakeId) || item;
  const file = take.name || take.path || item.name || '';
  if (!file) return '';
  const label = take.takeLabel && (item.takes?.length || 0) > 1 ? ` (${take.takeLabel})` : '';
  if (Number.isFinite(item.index)) return `Shot ${item.index}: ${file}${label}`;
  return `${file}${label}`;
}

export function buildVideoTakeChoiceDirection(item) {
  const take = item?.takes?.find((entry) => entry.id === item.selectedTakeId);
  const file = take?.name || take?.path || item?.name || '';
  if (!file || !Number.isFinite(item?.index)) return '';
  const label = take?.takeLabel || 'this take';
  return (
    `Use ${file} (${label}) for shot ${item.index} when you stitch the film. ` +
    `Keep every other take on disk, but concat this exact file for that shot — not a newer, larger, or later sibling. ` +
    `This is a take selection only, not approval to stitch. Do not stitch or render the final video until I explicitly approve the final stitch.`
  );
}

// Restore explicit studio selections from durable user turns or review answers, rather than
// treating the newest render as the user's choice after reopening the panel.
export function videoTakePicksFromEvents(events, project) {
  const picks = {};
  const items = [...(project?.clips || []), ...(project?.stills || [])];
  for (const event of Array.isArray(events) ? events : []) {
    const answered = event?.type === 'user_question_resolved';
    if (!answered && !['user_message', 'steer'].includes(event?.type)) continue;
    if (answered && (event.dismissed || event.skipped || event.timedOut)) continue;
    const text = String(answered ? event.answer || '' : event.text || event.message || event.content || '');
    const match = text.match(/^Use (.+?) \(([^)]+)\) (for shot (\d+) when you stitch the film|as the shot (\d+) keyframe|as the (.+?) (master|sheet) still)\./i);
    if (!match) continue;
    const file = match[1];
    const kind = match[4] ? 'clip' : 'still';
    const index = Number(match[4] || match[5]) || null;
    const item = items.find((candidate) => (
      (kind === 'clip' ? candidate.role === 'clip' : candidate.role !== 'clip')
      && (index ? candidate.index === index : candidate.character === match[6] && candidate.kind === match[7])
      && candidate.takes?.some((take) => (take.name || mediaBaseName(take)) === mediaBaseName({ name: file }))
    ));
    if (!item) continue;
    const take = item.takes.find((entry) => (entry.name || mediaBaseName(entry)) === mediaBaseName({ name: file }));
    picks[item.id] = {
      takeId: take.id, kind, index: item.index, role: item.kind, character: item.character, file, label: take.takeLabel,
      ...(Number.isFinite(event.seq) ? { seq: event.seq } : {}),
    };
  }
  return picks;
}

export function buildVideoStillTakeChoiceDirection(item) {
  const take = item?.takes?.find((entry) => entry.id === item.selectedTakeId);
  const file = take?.name || take?.path || item?.name || '';
  if (!file) return '';
  const label = take?.takeLabel || 'this take';
  const selectionOnly = ' This is a take selection only, not approval to animate. Do not generate clips or stitch the final video until I separately approve.';
  if (item?.kind === 'master' || item?.kind === 'sheet') {
    const who = item.character || 'character';
    return (
      `Use ${file} (${label}) as the ${who} ${item.kind} still. ` +
      `Keep every other take on disk, but use this exact file for identity — not a newer or later sibling.` + selectionOnly
    );
  }
  if (Number.isFinite(item?.index)) {
    return (
      `Use ${file} (${label}) as the shot ${item.index} keyframe. ` +
      `Keep every other take on disk, but use this exact source image — not a newer or later sibling.` + selectionOnly
    );
  }
  return `Use ${file} (${label}) as the still. Keep every other take on disk.` + selectionOnly;
}

export function collectVideoTakePicks(project) {
  return (project?.clips || [])
    .filter((clip) => clip.status === 'ready' && (clip.takes?.length || 0) > 0)
    .map((clip) => describeVideoTakeChoice(clip))
    .filter(Boolean);
}

// Stable clip IDs preserve shot identity while positions change. New clips append.
export function orderVideoClips(clips, order = []) {
  const remaining = new Map(clips.map((clip) => [clip.id, clip]));
  const result = [];
  for (const id of order) {
    if (!remaining.has(id)) continue;
    result.push(remaining.get(id));
    remaining.delete(id);
  }
  return [...result, ...remaining.values()];
}

export function videoTimelineOrderFromEvents(events) {
  let order = [];
  for (const event of Array.isArray(events) ? events : []) {
    if (!['user_message', 'steer', 'user_question_resolved'].includes(event?.type)) continue;
    const text = String(event.text || event.message || event.content || event.answer || '');
    const match = text.match(/^Video timeline order: (\[.*\])$/m);
    if (!match) continue;
    try {
      const parsed = JSON.parse(match[1]);
      if (Array.isArray(parsed) && parsed.every((id) => typeof id === 'string')
        && new Set(parsed).size === parsed.length) order = parsed;
    } catch (_) { /* Ignore incomplete history entries. */ }
  }
  return order;
}

export function buildVideoTimelineDirection(clips, picks = {}, { approve = false } = {}) {
  const included = clips.filter(clip => !clip.excluded);
  const excluded = clips.filter(clip => clip.excluded);
  const lines = included.map((clip, position) => {
    const lock = picks[clip.id];
    const latest = videoProjectFinalTake(clip) || clip;
    const file = lock?.file || latest.path || latest.name || clip.path || clip.name;
    const shot = Number.isFinite(clip.index) ? `Shot ${clip.index}` : clip.name;
    return `${position + 1}. ${shot}: ${file}${lock?.file ? ' (locked take)' : ''}`;
  });
  return [
    approve ? 'Approve clips — stitch the film.' : 'I changed the video timeline order.',
    `Video timeline order: ${JSON.stringify(clips.map((clip) => clip.id))}`,
    `Excluded video clips: ${JSON.stringify(excluded.map(clip => clip.id))}`,
    'Use this exact sequence from first to last, with these files:',
    ...lines,
    ...(excluded.length ? [
      `Excluded scenes: ${excluded.map(clip => Number.isFinite(clip.index) ? `Shot ${clip.index}` : clip.name).join(', ')}.`,
      'Omit every take of these scenes from the final video. Keep their source files so they can be included again later.',
    ] : []),
    'Stitch only the included clips listed above. Do not append other available clips.',
    'Timeline position overrides shot numbering, filename sorting, and any earlier sequence. Keep shot identities and locked takes unchanged.',
    approve
      ? 'Read this updated order before building the concat list and rendering the final film.'
      : 'Remember this order for the next stitch. This is an order update only; do not start rendering or generating clips until I approve the stitch.',
  ].join('\n');
}

export function buildVideoStitchDirectionFromPicks(picks) {
  const lines = Object.values(picks || {})
    .filter((pick) => pick?.file && Number.isFinite(pick.index) && pick.kind !== 'still')
    .sort((a, b) => (a.index || 0) - (b.index || 0))
    .map((pick) => `- Shot ${pick.index}: ${pick.file}${pick.label ? ` (${pick.label})` : ''}`);
  return [
    'Approve clips — stitch the film.',
    lines.length ? 'Use these exact files, one per shot:' : '',
    ...lines,
    lines.length
      ? 'For any other shot, use the latest take. Do not replace a locked file.'
      : 'I have not locked specific takes — use the latest take of each shot.',
  ].filter(Boolean).join('\n');
}

export function buildVideoStillsDirectionFromPicks(picks, { freezeReferences = false } = {}) {
  const lines = Object.values(picks || {})
    .filter((pick) => pick?.kind === 'still' && pick.file)
    .sort((a, b) => {
      const aKey = Number.isFinite(a.index) ? a.index : 1000;
      const bKey = Number.isFinite(b.index) ? b.index : 1000;
      if (aKey !== bKey) return aKey - bKey;
      return String(a.file).localeCompare(String(b.file));
    })
    .map((pick) => {
      const label = pick.label ? ` (${pick.label})` : '';
      if (pick.role === 'master' || pick.role === 'sheet') {
        const who = titleCaseWords(pick.character || 'Cast');
        return `- ${who} ${pick.role}: ${pick.file}${label}`;
      }
      if (Number.isFinite(pick.index)) return `- Shot ${pick.index} keyframe: ${pick.file}${label}`;
      return `- ${pick.file}${label}`;
    });
  return [
    'Approve stills — animate.',
    lines.length ? 'Use these exact stills:' : '',
    ...lines,
    freezeReferences
      ? 'These are the approved source images for this generation. Use only these exact files; do not substitute later takes.'
      : lines.length
      ? 'For any other still, use the latest take. Do not replace a locked still.'
      : 'I have not locked specific stills — use the latest take of each still.',
  ].filter(Boolean).join('\n');
}

export function buildVideoStitchDirection(project) {
  return buildVideoStitchDirectionFromPicks(
    Object.fromEntries(
      (project?.clips || [])
        .filter((clip) => clip.status === 'ready')
        .map((clip) => {
          const take = clip.takes?.find((entry) => entry.id === clip.selectedTakeId) || clip;
          return [clip.id, {
            index: clip.index,
            file: take.name || take.path || clip.name,
            label: take.takeLabel,
          }];
        }),
    ),
  );
}

export function buildVideoRedoDirection({ item, prompt = '', notes = '', kind = 'clip' } = {}) {
  const promptBlock = String(prompt || '').trim();
  const notesBlock = String(notes || '').trim();
  const extra = [
    promptBlock ? `Use this prompt:\n${promptBlock}` : '',
    notesBlock ? `Additional direction: ${notesBlock}` : '',
    'Export the new take as a distinct file and keep every previous take in the timeline.',
  ].filter(Boolean).join('\n');
  if (kind === 'still') {
    if (item?.kind === 'master' || item?.kind === 'sheet') {
      const who = item.character || 'character';
      return `Redo the ${who} ${item.kind} still. Do not animate yet.\n${extra}`;
    }
    if (Number.isFinite(item?.index)) {
      return `Redo shot ${item.index} keyframe. Do not animate yet.\n${extra}`;
    }
    return `Redo the still ${item?.name || ''}. Do not animate yet.\n${extra}`;
  }
  return `Redo shot ${item?.index}. Do not stitch the film yet.\n${extra}`;
}

export function videoProjectInventoryKey({ artifacts = [], files = [] } = {}) {
  const parts = [];
  for (const item of [...(artifacts || []), ...(files || [])]) {
    if (!item) continue;
    parts.push(`${mediaBaseName(item)}:${item.path || ''}:${Number(item.size) || 0}`);
  }
  return parts.join('|');
}

function collectUnique(items) {
  const out = [];
  const seen = new Map();
  for (const item of items || []) {
    if (!item) continue;
    const aliases = mediaAliasKeys(item);
    if (!aliases.length) continue;
    const existing = aliases.map((key) => seen.get(key)).find((idx) => idx !== undefined);
    if (existing !== undefined) {
      out[existing] = preferRawItem(out[existing], item);
      for (const key of aliases) seen.set(key, existing);
      continue;
    }
    const idx = out.length;
    out.push(item);
    for (const key of aliases) seen.set(key, idx);
  }
  return out;
}

export function collectVideoProjectFromMessages(messages = [], extras = {}) {
  const artifacts = [];
  const files = [];
  let scriptText = '';
  const considerScript = (text) => {
    const value = String(text || '').trim();
    if (!value) return;
    if (headingLooksLikeShot(value) && scriptQuality(value) >= scriptQuality(scriptText)) {
      scriptText = value;
    }
  };
  for (const message of Array.isArray(messages) ? messages : []) {
    if (Array.isArray(message?.parameters?.coworkerArtifacts)) {
      artifacts.push(...message.parameters.coworkerArtifacts);
    }
    if (Array.isArray(message?.files)) files.push(...message.files);
    if (Array.isArray(message?.images)) {
      for (const image of message.images) {
        if (!image) continue;
        files.push(typeof image === 'string'
          ? { cdnPath: image, url: image, fileType: 'image', source: 'inference' }
          : image);
      }
    }
    if (Array.isArray(message?.videos)) {
      for (const video of message.videos) {
        if (!video) continue;
        files.push(typeof video === 'string'
          ? { cdnPath: video, url: video, fileType: 'video', source: 'inference' }
          : video);
      }
    }
    if (message?.role === 'assistant') {
      considerScript(messagePlainText(message));
    }
  }
  considerScript(extras.summary);
  if (Array.isArray(extras.texts)) extras.texts.forEach(considerScript);
  return {
    artifacts: collectUnique(artifacts),
    files: collectUnique(files),
    scriptText,
  };
}

function fileBelongsToList(file, list) {
  if (!file || !Array.isArray(list)) return false;
  const keys = new Set(list.map(mediaKey).filter(Boolean));
  const name = mediaBaseName(file);
  if (mediaKey(file) && keys.has(mediaKey(file))) return true;
  return list.some((item) => mediaBaseName(item) === name && name);
}

export function fileBelongsToVideoProject(file, project) {
  if (!file || !project) return false;
  if (fileBelongsToList(file, project.artifacts) || fileBelongsToList(file, project.files)) return true;
  if (project.file && mediaKey(project.file) && mediaKey(project.file) === mediaKey(file)) return true;
  const name = mediaBaseName(file);
  if (name && mediaBaseName(project.file) === name) return true;
  return false;
}

function titleFromItems(film, clips, file) {
  if (film?.name) return film.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ');
  const named = mediaBaseName(file);
  if (named && !isScratchVideoClipName(named)) {
    return named.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ');
  }
  if (clips.length) return `${clips.length} shot${clips.length === 1 ? '' : 's'}`;
  return 'Video';
}

/**
 * Build the studio model: latest film, every export, ordered clips (including planned shots),
 * stills, and the shot the user clicked.
 */
export function buildVideoProject({
  file = null,
  artifacts = [],
  files = [],
  scriptText = '',
  selectedId = null,
} = {}) {
  const pool = collectUnique([
    ...(Array.isArray(artifacts) ? artifacts : []),
    ...(Array.isArray(files) ? files : []),
    file,
  ].filter(Boolean));

  const shots = parseShotScript(scriptText);
  const shotsByIndex = new Map(shots.map((shot) => [shot.index, shot]));

  const films = [];
  const segments = [];
  const clipCandidates = [];
  const stills = [];

  for (const item of pool) {
    if (isVideoMedia(item)) {
      // A downloaded video is not project media unless the agent flagged it
      // as studio work — otherwise it renders as a plain artifact.
      if (isStandaloneVideo(item)) continue;
      const role = classifyVideoRole(item);
      const media = toMediaItem(item, role);
      if (role === 'film') films.push(media);
      else if (role === 'segment') segments.push(media);
      else clipCandidates.push(media);
      continue;
    }
    const kind = classifyStillKind(item);
    if (!kind) continue;
    stills.push(toMediaItem(item, 'still', {
      kind,
      character: stillCharacterName(item),
      index: extractShotIndex(item),
    }));
  }

  const finals = sortFinals(films);
  // Keep the latest deliverable stable even while previewing an older export.
  const film = finals[0] || null;
  segments.sort((a, b) => (a.index ?? 0) - (b.index ?? 0) || a.name.localeCompare(b.name, undefined, { numeric: true }));

  const clipsByIndex = new Map();
  const unindexedClips = [];
  for (const clip of clipCandidates) {
    if (Number.isFinite(clip.index)) {
      pushTake(clipsByIndex, clip.index, clip);
    } else {
      unindexedClips.push(clip);
    }
  }

  const keyframesByIndex = new Map();
  const charactersByKey = new Map();
  const otherStills = [];
  for (const still of stills) {
    if (still.kind === 'keyframe' && Number.isFinite(still.index)) {
      pushTake(keyframesByIndex, still.index, still);
    } else if (still.kind === 'master' || still.kind === 'sheet') {
      const key = `${still.kind}:${(still.character || still.name).toLowerCase()}`;
      pushTake(charactersByKey, key, still);
    } else {
      otherStills.push(still);
    }
  }

  const decorateStillGroup = (takes, fallbackId) => {
    const decorated = decorateTakes(takes);
    const selected = decorated.takes.find((item) => item.id === decorated.selectedTakeId)
      || decorated.takes[decorated.takes.length - 1]
      || null;
    if (!selected) return null;
    const identity = extractCharacterIdentity(scriptText, selected.character);
    return {
      ...selected,
      id: fallbackId || stillGroupId(selected) || selected.id,
      mediaId: selected.id,
      prompt: identity || selected.prompt || '',
      takes: decorated.takes,
      selectedTakeId: decorated.selectedTakeId,
    };
  };

  const characters = [...charactersByKey.entries()]
    .map(([key, takes]) => decorateStillGroup(takes, `still:${key}`))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  const keyframes = [...keyframesByIndex.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([index, takes]) => {
      const group = decorateStillGroup(takes, `still:keyframe:${index}`);
      if (!group) return null;
      return { ...group, shot: shotsByIndex.get(index) || null };
    })
    .filter(Boolean);

  const indexes = new Set([
    ...clipsByIndex.keys(),
    ...keyframesByIndex.keys(),
    ...shotsByIndex.keys(),
  ]);
  const clips = [...indexes]
    .sort((a, b) => a - b)
    .map((index) => {
      const decorated = decorateTakes(clipsByIndex.get(index) || []);
      const media = decorated.takes.find((item) => item.id === decorated.selectedTakeId) || null;
      const still = keyframes.find((item) => item.index === index) || null;
      const shot = shotsByIndex.get(index) || null;
      const status = media ? 'ready' : still ? 'still' : 'planned';
      return {
        id: `shot:${index}`,
        index,
        name: media?.name || still?.name || `Shot ${index}`,
        path: media?.path || '',
        url: media?.url || '',
        mimeType: media?.mimeType || 'video/mp4',
        fileType: 'video',
        size: media?.size || 0,
        role: 'clip',
        kind: null,
        status,
        still,
        shot,
        source: media?.source || still?.source || null,
        takes: decorated.takes,
        selectedTakeId: decorated.selectedTakeId,
      };
    });

  for (const clip of unindexedClips) {
    clips.push({
      ...clip,
      status: 'ready',
      still: null,
      shot: null,
      takes: decorateTakes([clip]).takes,
      selectedTakeId: clip.id,
    });
  }

  const playable = [
    ...finals,
    ...segments.map((segment) => ({
      ...segment,
      status: 'ready',
      still: null,
      shot: shotsByIndex.get(segment.index) || null,
      label: Number.isFinite(segment.index) ? `Segment ${segment.index}` : segment.name,
    })),
    ...clips,
  ];

  const selectedFromFile = file
    ? playable.find((item) => fileBelongsToList(file, [
      item.source,
      item,
      ...(item.takes || []),
    ].filter(Boolean)))
      || playable.find((item) => mediaBaseName(item) === mediaBaseName(file))
    : null;
  const selected = playable.find((item) => item.id === selectedId)
    || selectedFromFile
    || film
    || [...clips].reverse().find((clip) => clip.status === 'ready')
    || playable[0]
    || null;

  return {
    title: titleFromItems(film, clips, file),
    film,
    finals,
    segments,
    clips,
    stills: [...characters, ...keyframes, ...otherStills],
    characters,
    keyframes,
    shots,
    playable,
    selectedId: selected?.id || null,
    clipCount: Math.max(clips.length, shots.length),
    readyCount: clips.filter((clip) => clip.status === 'ready').length,
  };
}

function eventBlob(event) {
  const options = Array.isArray(event?.options)
    ? event.options.map((option) => optionLabel(option)).join(' ')
    : '';
  return [
    event?.question,
    event?.answer,
    event?.optionId,
    event?.text,
    event?.message,
    event?.content,
    options,
  ].filter(Boolean).join(' ');
}

function isStillsReviewQuestion(event) {
  const blob = eventBlob(event).toLowerCase();
  return /approve stills|stills look consistent|before work animates|character looks like the same person|approve (?:the )?keyframes|keyframes are ready for review/.test(blob);
}

function isStillsApproveText(text, optionId) {
  const blob = `${text || ''} ${optionId || ''}`.toLowerCase();
  if (/take selection only|not (?:an? )?approval|(?:do not|don't|never) (?:approve|animate|generate clips)|until i (?:separately|explicitly) approve/.test(blob)) return false;
  if (/redo/.test(blob)) return false;
  if (/approve stills/.test(blob)) return true;
  return /approve/.test(blob) && /animate/.test(blob);
}

function isClipAnimationEvent(event) {
  const name = String(event?.tool || event?.name || event?.toolName || '').toLowerCase();
  if (name === 'image_to_video' || name === 'generate_video') return true;
  return /animating an image/.test(eventBlob(event).toLowerCase());
}

/**
 * Queued composer lines from earlier video gates. After stills exist or
 * animation has started, auto-flushing "Approve script — build the stills"
 * (or a leftover stills-approve) would send the agent backwards.
 */
export function isObsoleteVideoFollowUp(text, { events, project } = {}) {
  const blob = String(text || '').toLowerCase();
  if (!blob) return false;
  const stillsApproved = videoProjectStillsApprovedFromEvents(events);
  const hasStills = (project?.stills?.length || 0) > 0;
  const hasClips = (project?.readyCount || 0) > 0;
  const animated = stillsApproved || hasClips
    || (Array.isArray(events) && events.some(isClipAnimationEvent));
  if (/approve script/.test(blob) && (hasStills || animated)) return true;
  if (/approve stills/.test(blob) && animated) return true;
  return false;
}

/**
 * True once the user already approved stills (ask_user answer or a steer
 * like "Approve stills — animate"). A later stills-review question reopens
 * the gate. Script / spend "Approve" answers do not count.
 */
export function videoProjectStillsApprovedFromEvents(events) {
  let pendingStills = false;
  let approved = false;
  for (const event of Array.isArray(events) ? events : []) {
    const type = event?.type;
    if (type === 'user_question_requested') {
      pendingStills = isStillsReviewQuestion(event);
      if (pendingStills) approved = false;
      continue;
    }
    if (type === 'user_question_resolved') {
      if (pendingStills && !event.dismissed && !event.skipped && !event.timedOut) {
        const text = String(event.answer || event.optionId || '');
        if (/redo/i.test(text)) approved = false;
        else if (isStillsApproveText(text, event.optionId) || /^approve\b/i.test(text.trim())) {
          approved = true;
        }
      }
      pendingStills = false;
      continue;
    }
    if (type === 'user_message' || type === 'steer') {
      const text = String(event.text || event.message || event.content || '');
      if (isStillsApproveText(text)) approved = true;
      continue;
    }
    if (isClipAnimationEvent(event)) approved = true;
  }
  return approved;
}

export function videoProjectCanReviewClips(project) {
  return !!(project
    && project.clipCount >= 2
    && project.readyCount >= project.clipCount
    && !project.film
    && !project.finals?.length);
}

// UI activity state only; the host's videoStitchApproval remains authoritative
// for permission to render. Follow both chat directions and review-card answers.
export function videoProjectStitchPendingFromEvents(events) {
  const stitch = /\b(?:stitch|assemble|concatenate|combine)\b.{0,45}\b(?:film|video|episode|clips?|takes?|final cut|it|them|everything)\b/i;
  const refusal = /\b(?:no|not|never|don['’]?t|do not|wait|hold|before|until|unless|when|after|if|once|provided|assuming|later|yet|redo|regenerate|instead)\b/i;
  const approves = value => {
    const text = String(value || '').trim();
    const lead = text.match(/^[^.!?\n]*[.!?]?/)?.[0] || text;
    if (!lead || refusal.test(lead) || /\?/.test(lead)
      || /\b(?:do not|don['’]?t|never|stop|wait|hold)\b[^.\n]{0,40}\b(?:stitch|assembl|render|combin|concat|proceed|continu|start|begin)/i.test(text)
      || /(?:^|[.!?\n])\s*(?:(?:but|actually|however)[,\s]+)?(?:wait|hold off|not yet)\b/i.test(text)) return false;
    return /^(?:i )?(?:approve|authorize) (?:the )?final (?:stitch|assembly)[.!]?$/i.test(lead)
      || /^(?:(?:yes|ok|okay|approved?)[,.!\s—–:-]+)?(?:(?:please|now|go ahead and|you can|you may)\s+)?(?:stitch|assemble|concatenate|combine)\b/i.test(text) && stitch.test(text)
      || /^approve (?:the )?(?:clips|takes|final (?:stitch|assembly|cut))\b/i.test(text) && stitch.test(text);
  };
  let pending = false;
  let question = null;
  for (const event of events || []) {
    if (event.type === 'user_message' || event.type === 'steer') {
      pending = pending || approves(event.text || event.message || event.content);
      question = null;
    }
    if (event.type === 'user_question_requested') {
      const text = String(event.question || '').replace(/\bno[\s-]+(?:audio(?:\s+tracks?)?|sound|dialogue|text\s+overlays?|captions?|subtitles?)\b/gi, '');
      const combined = [text, ...(event.options || []).map(option => option.label)].join(' ');
      const offersStitch = !refusal.test(text) || text.split(/\bor\b/i).some(clause => !refusal.test(clause) && (stitch.test(clause) || /\bfinal (?:stitch|assembly)\b/i.test(clause)));
      question = !['cost', 'credits', 'memory'].includes(event.kind) && !event.questions?.length
        && !/\b(?:script|storyboard|stills?|keyframes?|credits?|spend|cost|generate|animate)\b/i.test(text)
        && (stitch.test(combined) || /\bfinal (?:stitch|assembly)\b/i.test(combined))
        && offersStitch ? event : null;
    }
    if (event.type === 'user_question_resolved' && question?.id === event.id && question) {
      const answer = String(event.answer || question.options?.find(option => option.id === event.optionId)?.label || '').trim();
      pending = !event.skipped && !event.timedOut && !event.dismissed && (approves(answer)
        || (!refusal.test(question.question.replace(/\bno[\s-]+(?:audio(?:\s+tracks?)?|sound|dialogue|text\s+overlays?|captions?|subtitles?)\b/gi, '').split(/\bor\b/i)[0]) && /^(?:yes|y|approve|approved|approve clips|go ahead|proceed|stitch|stitch now)[.!]?$/i.test(answer)));
      question = null;
    }
    if (event.type === 'video_timeline_updated') question = null;
    if (event.type === 'session_end'
      || (['tool_execution_start', 'tool_execution_end'].includes(event.type) && ['image_to_video', 'generate_video', 'extend_video'].includes(event.toolName || event.name))) {
      pending = false;
      question = null;
    }
    if (event.type === 'artifact_ready' && event.artifact && isVideoArtifact(event.artifact)
      && classifyVideoRole(event.artifact) === 'film') pending = false;
  }
  return pending;
}

export function videoProjectCanReviewStills(project, { events } = {}) {
  if (!project || project.film || project.finals?.length || project.readyCount !== 0 || !project.stills?.length) return false;
  if (videoProjectStillsApprovedFromEvents(events)) return false;
  const plannedShots = Math.max(project.shots?.length || 0, project.clipCount || 0);
  if (plannedShots > 0) return (project.keyframes?.length || 0) >= plannedShots;
  // No shot script: only a genuine keyframe set is reviewable film material.
  // Every non-keyframe image classifies as a fallback 'still', so a thread
  // that delivered ONE plain image (homesteading scene, 6a9a638f) must not
  // surface an "Approve stills — animate / Redo a keyframe" card the moment
  // it goes idle.
  return (project.keyframes?.length || 0) > 0;
}

export function videoProjectReviewGate(project, options) {
  if (videoProjectCanReviewClips(project)) return 'clips';
  if (videoProjectCanReviewStills(project, options)) return 'stills';
  return null;
}

/** Orphaned composer card when Work finished clips/stills but never asked. */
export function workVideoReviewQuestion(project, options) {
  const gate = videoProjectReviewGate(project, options);
  if (gate === 'clips') {
    return {
      id: `video-review-clips:${project.readyCount}`,
      kind: 'choice',
      orphaned: true,
      question: 'Do you approve these clips?',
      allowCustom: true,
      options: [
        { id: 'approve', label: 'Approve clips — stitch the film' },
        { id: 'redo', label: 'Redo a clip' },
      ],
    };
  }
  if (gate === 'stills') {
    return {
      id: `video-review-stills:${project.stills.length}`,
      kind: 'choice',
      orphaned: true,
      question: 'Do these stills look consistent?',
      allowCustom: true,
      options: [
        { id: 'approve', label: 'Approve stills — animate' },
        { id: 'redo-character', label: 'Redo a character' },
        { id: 'redo-keyframe', label: 'Redo a keyframe' },
      ],
    };
  }
  return null;
}
